import jwtAxios from '@nxsys/services/axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { endPoints } from '@nxsys/services/endpoints';
import {
  ApplyHMRCP6p9Response,
  ApplySingleHMRCParams,
  CancelHMRCP6p9Response,
  GetHMRCP6p9UpdatesParams,
  GetHMRCP6p9UpdatesResponse,
  GetStudentLoanUpdatesParams,
  GetStudentLoanUpdatesResponse,
  HMRCSyncParamsType,
} from '../types';

export const getHMRCP6p9Pending = createAsyncThunk<
  GetHMRCP6p9UpdatesResponse,
  GetHMRCP6p9UpdatesParams
>('hmrcUpdates/getHMRCP6p9Pending', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.hmrc.p6p9, {
      params: {
        ...params,
        status: 'PENDING',
      },
    });
    return {
      data: response.data.data,
      pagination: {
        current: params.page || 0,
        pageSize: params.size || 20,
        totalDocs: response.data.totalDocs,
      },
      queryParams: params,
    };
  } catch (error: any) {
    throw rejectWithValue(error.response?.data || error.message);
  }
});

export const getHMRCP6p9AppliedCancelled = createAsyncThunk<
  GetHMRCP6p9UpdatesResponse,
  GetHMRCP6p9UpdatesParams
>(
  'hmrcUpdates/getHMRCP6p9AppliedCancelled',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.hmrc.p6p9, { params });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      throw rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const applyHMRCP6p9 = createAsyncThunk<
  ApplyHMRCP6p9Response,
  { ids: number[] }
>('hmrcUpdates/applyHMRCP6p9', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.put(
      endPoints.hmrcRequest.P6P9.apply,
      params.ids,
    );
    return response.data;
  } catch (error: any) {
    throw rejectWithValue(error.response?.data || error.message);
  }
});

export const applySingleHMRCP6p9 = createAsyncThunk<
  ApplyHMRCP6p9Response,
  ApplySingleHMRCParams
>('hmrcUpdates/applySingleHMRCP6p9', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.put(
      endPoints.hmrcRequest.P6P9.applySingle,
      params,
    );
    return response.data;
  } catch (error: any) {
    throw rejectWithValue(error.response?.data || error.message);
  }
});

export const cancelHMRCP6p9 = createAsyncThunk<
  CancelHMRCP6p9Response,
  { ids: number[] }
>('hmrcUpdates/cancelHMRCP6p9', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.put(
      endPoints.hmrcRequest.P6P9.cancel,
      params.ids,
    );
    return response.data;
  } catch (error: any) {
    throw rejectWithValue(error.response?.data || error.message);
  }
});

export const hmrcSync = createAsyncThunk<any, HMRCSyncParamsType>(
  'hmrcUpdates/hmrcSync',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.hmrcRequest.request.sync, {
        hmrcDataType: params.hmrcDataType,
      });
      return response.data;
    } catch (error: any) {
      throw rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Student Loan

export const getStudentLoanPending = createAsyncThunk<
  GetStudentLoanUpdatesResponse,
  GetStudentLoanUpdatesParams
>('hmrcUpdates/getStudentLoanPending', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.hmrc.studentLoan, {
      params: {
        ...params,
        status: 'PENDING',
      },
    });
    return {
      data: response.data.data,
      pagination: {
        current: params.page || 0,
        pageSize: params.size || 20,
        totalDocs: response.data.totalDocs,
      },
      queryParams: params,
    };
  } catch (error: any) {
    throw rejectWithValue(error.response?.data || error.message);
  }
});

export const getStudentLoanAppliedCancelled = createAsyncThunk<
  GetStudentLoanUpdatesResponse,
  GetStudentLoanUpdatesParams
>(
  'hmrcUpdates/getStudentLoanAppliedCancelled',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.hmrc.studentLoan, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      throw rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const applyStudentLoan = createAsyncThunk<
  ApplyHMRCP6p9Response,
  { ids: number[] }
>('hmrcUpdates/applyStudentLoan', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.put(
      endPoints.hmrcRequest.studentLoan.apply,
      params.ids,
    );
    return response.data;
  } catch (error: any) {
    throw rejectWithValue(error.response?.data || error.message);
  }
});

export const applySingleStudentLoan = createAsyncThunk<
  ApplyHMRCP6p9Response,
  ApplySingleHMRCParams
>('hmrcUpdates/applySingleStudentLoan', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.put(
      endPoints.hmrcRequest.studentLoan.applySingle,
      params,
    );
    return response.data;
  } catch (error: any) {
    throw rejectWithValue(error.response?.data || error.message);
  }
});

export const cancelStudentLoan = createAsyncThunk<
  CancelHMRCP6p9Response,
  { ids: number[] }
>('hmrcUpdates/cancelStudentLoan', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.put(
      endPoints.hmrcRequest.studentLoan.cancel,
      params.ids,
    );
    return response.data;
  } catch (error: any) {
    throw rejectWithValue(error.response?.data || error.message);
  }
});

export const hmrcUpdatesAct = {
  getHMRCP6p9Pending,
  getHMRCP6p9AppliedCancelled,
  applyHMRCP6p9,
  applySingleHMRCP6p9,
  cancelHMRCP6p9,
  hmrcSync,
  getStudentLoanPending,
  getStudentLoanAppliedCancelled,
  applyStudentLoan,
  applySingleStudentLoan,
  cancelStudentLoan,
};
